import { useMemo, useState } from 'react';

import { Badge, Button } from '@/components/atoms';
import {
  useCreateCredential,
  useCredentials,
  useProviderSettings,
  useRotateCredential,
  useUpsertProviderSetting,
} from '@/hooks/useConfig';
import { ApiError } from '@/services/api';
import type { Credential, ProviderSetting } from '@/types/config';

import styles from './ConfigExternalApis.module.css';

interface ConfigField {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'password';
  hint?: string;
  placeholder?: string;
}

interface ExternalApiSpec {
  providerKey: string;
  label: string;
  description: string;
  secretLabel: string;
  configFields: ConfigField[];
}

const EXTERNAL_APIS: ExternalApiSpec[] = [
  {
    providerKey: 'serp',
    label: 'SERP API',
    description:
      'Google Trends, People-Also-Ask, busca orgânica. Usado em Trending Topics e SEO panel.',
    secretLabel: 'API key',
    configFields: [
      {
        key: 'provider_kind',
        label: 'Provider escolhido',
        type: 'text',
        hint: 'serpapi | searchapi  (define qual conector responde)',
        placeholder: 'serpapi',
      },
      { key: 'default_country', label: 'Default country', placeholder: 'br' },
      { key: 'default_language', label: 'Default language', placeholder: 'pt' },
      {
        key: 'default_location',
        label: 'Default location',
        placeholder: 'Brazil',
      },
      {
        key: 'cache_ttl_minutes',
        label: 'Cache TTL (min)',
        type: 'number',
        placeholder: '30',
      },
    ],
  },
  {
    providerKey: 'smtp',
    label: 'SMTP (notificações email)',
    description:
      'Servidor SMTP para o canal "email" das notificações. Recomendado: Amazon SES, SendGrid, Postmark.',
    secretLabel: 'Password',
    configFields: [
      {
        key: 'host',
        label: 'Host',
        placeholder: 'email-smtp.us-east-1.amazonaws.com',
      },
      { key: 'port', label: 'Port', type: 'number', placeholder: '587' },
      { key: 'user', label: 'User', placeholder: 'AKIA...' },
      {
        key: 'from_address',
        label: 'Remetente',
        placeholder: 'noreply@wiredesk.tld',
      },
      {
        key: 'use_tls',
        label: 'TLS (true/false)',
        type: 'text',
        placeholder: 'true',
      },
    ],
  },
  {
    providerKey: 'twitter',
    label: 'Twitter / X',
    description:
      'Publicação automática de tweets a partir de stories. v2 API com OAuth1 + Bearer.',
    secretLabel: 'Bearer token',
    configFields: [
      { key: 'api_key', label: 'API key' },
      { key: 'api_secret', label: 'API secret' },
      { key: 'access_token', label: 'Access token' },
      { key: 'access_token_secret', label: 'Access token secret' },
      { key: 'username', label: 'Username', placeholder: '@gncnews' },
    ],
  },
  {
    providerKey: 'freeastrology',
    label: 'FreeAstrology API',
    description:
      'API de horóscopos diários. Usado pelo Horóscopo Worker. Migrar do nó hardcoded no N8N.',
    secretLabel: 'API key',
    configFields: [
      { key: 'default_lang', label: 'Default language', placeholder: 'pt' },
      {
        key: 'timezone',
        label: 'Timezone',
        placeholder: 'America/Sao_Paulo',
      },
    ],
  },
];

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const detail = (e.body as { detail?: string } | null)?.detail;
    if (detail) return detail;
    return `${e.status} ${e.message}`;
  }
  return String(e);
}

export function ConfigExternalApis() {
  const { data: settings = [] } = useProviderSettings();
  const { data: credentials = [] } = useCredentials();
  const createCred = useCreateCredential();
  const rotateCred = useRotateCredential();
  const upsertSetting = useUpsertProviderSetting();

  const settingByProvider = useMemo(
    () =>
      Object.fromEntries(
        settings.map((s: ProviderSetting) => [s.provider, s]),
      ),
    [settings],
  );
  const credByProvider = useMemo(
    () =>
      Object.fromEntries(
        credentials.map((c: Credential) => [c.provider, c]),
      ),
    [credentials],
  );

  const [editing, setEditing] = useState<ExternalApiSpec | null>(null);
  const [secret, setSecret] = useState('');
  const [configForm, setConfigForm] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<string | null>(null);

  function openEdit(spec: ExternalApiSpec) {
    const existing = settingByProvider[spec.providerKey];
    const cfg = (existing?.config ?? {}) as Record<string, unknown>;
    const draft: Record<string, string> = {};
    for (const field of spec.configFields) {
      const value = cfg[field.key];
      draft[field.key] = value === undefined || value === null ? '' : String(value);
    }
    setEditing(spec);
    setConfigForm(draft);
    setSecret('');
    setMsg(null);
  }

  function closeEdit() {
    setEditing(null);
    setConfigForm({});
    setSecret('');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setMsg(null);

    // Coerce numeric fields to numbers where it makes sense
    const configPayload: Record<string, unknown> = {};
    for (const field of editing.configFields) {
      const raw = configForm[field.key]?.trim();
      if (!raw) continue;
      if (field.type === 'number') {
        const n = Number(raw);
        if (Number.isFinite(n)) configPayload[field.key] = n;
      } else {
        configPayload[field.key] = raw;
      }
    }

    try {
      await upsertSetting.mutateAsync({
        provider: editing.providerKey,
        config: configPayload,
      });

      if (secret) {
        const existingCred = credByProvider[editing.providerKey];
        if (existingCred) {
          await rotateCred.mutateAsync({
            id: existingCred.id,
            payload: { secret, reason: 'updated via external-apis page' },
          });
        } else {
          await createCred.mutateAsync({
            provider: editing.providerKey,
            slug: 'main',
            secret,
            notes: `created via external-apis page`,
          });
        }
      }

      setMsg(`${editing.label} actualizado.`);
      closeEdit();
    } catch (err) {
      setMsg(`Erro: ${describeError(err)}`);
    }
  }

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <h1 className={styles.title}>APIs externas</h1>
        <p className={styles.subtitle}>
          SERP, SMTP, Twitter/X, FreeAstrology e outros serviços de terceiros.
          As chaves ficam Fernet-encrypted; as configs vão para
          <code> provider_setting</code>.
        </p>
      </header>

      {msg && <p className={styles.flash}>{msg}</p>}

      <ul className={styles.list}>
        {EXTERNAL_APIS.map((spec) => {
          const cred = credByProvider[spec.providerKey] as Credential | undefined;
          const setting = settingByProvider[spec.providerKey] as
            | ProviderSetting
            | undefined;
          return (
            <li key={spec.providerKey} className={styles.card}>
              <div className={styles.cardHead}>
                <span className={styles.cardTitle}>{spec.label}</span>
                {cred?.has_secret ? (
                  <Badge tone="ok" size="sm">
                    chave configurada
                  </Badge>
                ) : (
                  <Badge tone="warn" size="sm">
                    sem chave
                  </Badge>
                )}
                {setting && (
                  <Badge tone="info" size="sm">
                    config: {Object.keys(setting.config).length} keys
                  </Badge>
                )}
              </div>
              <p className={styles.desc}>{spec.description}</p>

              {cred && (
                <div className={styles.row}>
                  <span className={styles.label}>{spec.secretLabel}</span>
                  <span className={styles.mono}>{cred.secret_masked}</span>
                </div>
              )}

              {setting && Object.keys(setting.config).length > 0 && (
                <div className={styles.cfgGrid}>
                  {Object.entries(setting.config).map(([k, v]) => (
                    <div key={k} className={styles.cfgRow}>
                      <span className={styles.cfgKey}>{k}</span>
                      <span className={styles.cfgValue}>{String(v)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.actions}>
                <Button size="sm" variant="secondary" onClick={() => openEdit(spec)}>
                  Configurar
                </Button>
              </div>
            </li>
          );
        })}
      </ul>

      {editing && (
        <div className={styles.modalBackdrop} onClick={closeEdit}>
          <form
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
          >
            <h2 className={styles.modalTitle}>{editing.label}</h2>
            <p className={styles.modalSub}>{editing.description}</p>

            <label className={styles.field}>
              <span>
                {editing.secretLabel}
                {credByProvider[editing.providerKey] ? ' (deixar vazio mantém o actual)' : ''}
              </span>
              <input
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                autoComplete="new-password"
              />
            </label>

            {editing.configFields.map((field) => (
              <label key={field.key} className={styles.field}>
                <span>{field.label}</span>
                <input
                  type={field.type ?? 'text'}
                  value={configForm[field.key] ?? ''}
                  onChange={(e) =>
                    setConfigForm({
                      ...configForm,
                      [field.key]: e.target.value,
                    })
                  }
                  placeholder={field.placeholder}
                />
                {field.hint && (
                  <span className={styles.hint}>{field.hint}</span>
                )}
              </label>
            ))}

            <div className={styles.modalActions}>
              <Button type="button" variant="ghost" onClick={closeEdit}>
                Cancelar
              </Button>
              <Button
                type="submit"
                loading={
                  upsertSetting.isPending ||
                  createCred.isPending ||
                  rotateCred.isPending
                }
              >
                Guardar
              </Button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
